/** @format */

/* eslint @typescript-eslint/non-nullable-type-assertion-style: "warn" */
import { ReactNode, isValidElement } from 'react';
import { css } from 'styled-components';

import {
    largeTablet,
    mobile,
    smallMobile,
    smallTablet,
} from '@toucan/helpers/media-queries';

import { ColumnVariant } from '@toucan/client/shared/components/column';

import { RowStackVariant, RowStyleProps } from './index';

/**
 * Gets the grid-template-columns value
 *
 * @param columns The columns for the grid
 * @param addGridGaps If should add in grid gaps as extra columns. Used for IE
 */
export function getGridColumns(
    columns: ReactNode[],
    addGridGaps: boolean = false
): string {
    return columns
        .flatMap((column, i) => {
            if (!isValidElement(column) || !column.props.variant) {
                return 'false'; // keeps ts happy by using a string 🤷‍♂️
            }

            /**
             * Setting a min value of the column to 0 so the content can't
             * break the layout. By default the min width of column is auto which
             * is based on the content inside
             */
            const value =
                column.props.variant === ColumnVariant.FLUID
                    ? `minmax(0, ${column.props.fr ?? 1}fr)`
                    : 'minmax(0, 34rem)';

            if (addGridGaps && i !== columns.length - 1) {
                return [value, 'var(--layout-spacing-24)'];
            } else {
                return value;
            }
        })
        .filter((val) => val !== 'false')
        .join(' ');
}

function getStackVariant(props: RowStyleProps) {
    const variant = props.stackVariant as RowStackVariant;
    const styles = css`
        display: block;

        > * {
            margin-bottom: ${props.stackGap ?? '0'};
            &:last-child {
                margin-bottom: 0;
            }
        }
    `;
    switch (variant) {
        case RowStackVariant.DESKTOP:
            return styles;

        case RowStackVariant.LARGE_TABLET:
            return largeTablet(styles);
        case RowStackVariant.SMALL_TABLET:
            return smallTablet(styles);
        case RowStackVariant.MOBILE:
            return mobile(styles);
        case RowStackVariant.SMALL_MOBILE:
            return smallMobile(styles);
    }
}

/**
 * Base styles for the row component
 *
 * @param {RowStyleProps} props The row properties object
 * */
const baseStyles = (props: RowStyleProps) => css`
    display: grid;
    grid-template-columns: ${getGridColumns(props.columns)};
    grid-gap: var(--layout-spacing-24);

    @media (-ms-high-contrast: none) {
        display: -ms-grid;
        -ms-grid-columns: ${getGridColumns(props.columns, true)};

        ${props.columns.map((column, i) => {
            return css`
                > *:nth-of-type(${i + 1}) {
                    /* 2n - 1 */
                    -ms-grid-column: ${2 * (i + 1) - 1};
                }
            `;
        })}
    }

    ${getStackVariant(props)}
`;

/**
 * The row component styles
 *
 * @param {RowStyleProps} props The row properties object
 * */
export const styles = (props: RowStyleProps) => css`
    ${baseStyles(props)}
`;
